import { useMemo } from 'react'
import {
  GeckoAggregation,
  GeckoComponentDataFrame,
  GeckoInspection,
  GeckoSource,
} from 'types'

/**
 * Hook to filter and organize inspections and CDFs for a component.
 * Takes all inspections and CDFs as input parameters to avoid boundary violations.
 */
export function useComponentInspectionsData(
  componentId: string | undefined,
  allInspections: ReadonlyArray<GeckoInspection>,
  allCdfs: ReadonlyArray<GeckoComponentDataFrame>
): {
  inspections: ReadonlyArray<GeckoInspection>
  inspectionCdfs: Map<string, GeckoComponentDataFrame[]>
} {
  // Filter inspections for this component and sort by date (newest first)
  const sortedInspections = useMemo(() => {
    return [...allInspections]
      .filter((i) => i.tags.component === componentId)
      .sort((a, b) => b.data.date.getTime() - a.data.date.getTime())
  }, [allInspections, componentId])

  // Group thickness CDFs by inspection ID
  const inspectionCdfs = useMemo(() => {
    const map = new Map<string, GeckoComponentDataFrame[]>()

    allCdfs
      .filter((cdf) => {
        // Only include thickness CDFs from inspections (not projections/nominal)
        const isThickness = cdf.data.params.view?.id === 'thickness'
        const isNotProjection =
          cdf.data.params.source !== GeckoSource.Projection
        const isNotTMin = cdf.data.params.source !== GeckoSource.TMin
        const isNotNominal = cdf.data.params.source !== GeckoSource.Nominal
        const hasInspection = !!cdf.tags.inspection
        // Must be for this component
        const isForComponent = cdf.tags.component === componentId

        return (
          isThickness &&
          isNotProjection &&
          isNotTMin &&
          isNotNominal &&
          hasInspection &&
          isForComponent
        )
      })
      .forEach((cdf) => {
        const inspectionId = cdf.tags.inspection
        if (inspectionId) {
          const existing = map.get(inspectionId) ?? []
          map.set(inspectionId, [...existing, cdf])
        }
      })

    // Sort CDFs within each inspection by preference (min aggregation preferred)
    map.forEach((cdfs, key) => {
      map.set(
        key,
        cdfs.sort((a, b) => {
          // Prefer minimum aggregation
          if (
            a.data.params.aggregation === GeckoAggregation.Minimum &&
            b.data.params.aggregation !== GeckoAggregation.Minimum
          ) {
            return -1
          }
          if (
            b.data.params.aggregation === GeckoAggregation.Minimum &&
            a.data.params.aggregation !== GeckoAggregation.Minimum
          ) {
            return 1
          }
          return 0
        })
      )
    })

    return map
  }, [allCdfs, componentId])

  return useMemo(
    () => ({
      inspections: sortedInspections,
      inspectionCdfs,
    }),
    [sortedInspections, inspectionCdfs]
  )
}

/**
 * Get the best thickness CDF for an inspection
 */
export function getBestThicknessCdf(
  inspectionId: string,
  inspectionCdfs: Map<string, GeckoComponentDataFrame[]>
): GeckoComponentDataFrame | undefined {
  const cdfs = inspectionCdfs.get(inspectionId)
  return cdfs?.[0]
}
